import React, { useRef } from 'react'
import { useIntl } from 'react-intl'
import HeadData from 'components/Common/HeadData'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import List from 'components/Common/List'
import LayoutContainer from 'components/Common/LayoutContainer'
import Hr from 'components/Common/Hr'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Faq from 'components/Common/Faq'
import IconedCallout from 'components/Common/IconedCallout'
import HandpickedPartnersIcon from 'LuxKit/Icons/custom/HandpickedPartnersIcon'
import OfferListHeading from 'components/OfferList/OfferListHeading'
import useContent from './content'
import { connect } from 'react-redux'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import styled from 'styled-components'
import { rem } from 'polished'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import IconButton from 'LuxKit/Button/IconButton'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import { FLASH_OFFERS_TYPES } from 'constants/offerList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'

const ImageContent = styled(LayoutContainer)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(32)};
`

const ImageWrapper = styled.div`
  width: 100%;
  overflow: hidden;
`

const Wrapper = styled.div`
  position: relative;
  min-height: 1px;
`

const ContainerArrow = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  text-align: center;
  padding: ${rem(4)};
`

const SameHeightPaneSm = styled(Pane)`
  margin-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    margin-top: 0;
    height: 100%;
  }
`

const ThreeColumnGrid = styled.div`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

interface Props {
  phone: App.GeoContacts
}

const listFilters: App.OfferListFilters = {
  offerTypes: FLASH_OFFERS_TYPES,
  frontPageVisibleOnly: true,
}

function BestPricePage(props: Props) {
  const { phone } = props
  const intl = useIntl()
  const content = useContent()
  const topSection = useRef<HTMLDivElement>(null)
  const thisWeeksBestDealsTitle = intl.formatMessage({
    id: '7wGuj1FXP/',
    defaultMessage: 'This Weeks Best Deals',
    description: 'Heading for the weekly best deals offer list on the best price guarantee page',
  })
  const offerListExtras: Array<OfferListV2Item> = [
    {
      id: 'heading',
      position: 'start',
      element: <OfferListHeading title={thisWeeksBestDealsTitle} />,
      show: 'hasOffers',
    },
    {
      id: 'see_more_details',
      position: 4,
      element: <Group
        key="see_more_deals"
        direction="horizontal"
        horizontalAlign="center"
      >
        <TextButton
          kind="primary"
          fit="mobile-full-width"
          to="/"
        >
          {intl.formatMessage({
            id: 'yjf517FxKg',
            defaultMessage: 'Discover more great deals',
            description: 'Button text to navigate to home page to see more deals on best price page',
          })}
        </TextButton>
      </Group>,
      show: 'always',
    },
  ]
  return (
    <>
      <HeadData
        title={content.header.title}
        description={content.header.subTitle}
      />
      <Wrapper>
        <ImageWrapper>
          <ResponsiveImage
            alt={content.header.title}
            aria-hidden="true"
            brightness={-35}
            gravity="auto"
            mobileAspectRatio="8:9"
            tabletAspectRatio="2:1"
            largeDesktopAspectRatio="8:3"
            id="BestPriceHero_dzacxj"
          />
        </ImageWrapper>
        <ImageContent size="sm">
          <Heading variant="heading2" align="center" colour="neutral-eight" as="h1">
            {content.header.title}
          </Heading>
          <BodyText variant="large" align="center" colour="neutral-eight" as="h2">
            {content.header.subTitle}
          </BodyText>
        </ImageContent>
        <ContainerArrow>
          <IconButton
            kind="tertiary"
            variant="ghost"
            size="large"
            shape="circle"
            onClick={() => topSection.current?.scrollIntoView()}
          >
            <LineAngleDownIcon />
          </IconButton>
        </ContainerArrow>
      </Wrapper>
      <Pane
        ref={topSection}
      >
        <PaneBody size="xl-y md">
          <Group direction="horizontal" horizontalAlign="center">
            <HandpickedPartnersIcon size={87} colour="primary" />
          </Group>
          <Heading variant="heading3" align="center" as="h2">
            {content.negotiation.title}
          </Heading>
          <BodyText variant="large" weight="bold" align="center">
            <br />
            {content.negotiation.subTitle}
          </BodyText>
          <LayoutContainer size="sm">
            <List type="unstyled" spaced>
              {content.negotiation.listItems.map((listItem, i) => (
                <li key={i}>
                  <Group direction="horizontal" gap={16}>
                    <div>{listItem.icon}</div>
                    {listItem.text}
                  </Group>
                </li>
              ))}
            </List>
          </LayoutContainer>
        </PaneBody>
      </Pane>
      <Pane background="neutral-six">
        <PaneBody size="xl-y md">
          <LayoutContainer>
            <Heading variant="heading3" align="center" as="h2">{content.howItWorks.title}</Heading>
            <BodyText variant="large" align="center" as="p">{content.howItWorks.subTitle}</BodyText>
            <PaneBody size="xl-y">
              <ThreeColumnGrid>
                {content.howItWorks.items.map((item, i) =>
                  <div key={i}>
                    <SameHeightPaneSm>
                      <PaneBody size="lg xl-y">
                        <IconedCallout
                          icon={item.icon}
                          title={item.title}
                          text={item.text}
                        />
                      </PaneBody>
                    </SameHeightPaneSm>
                  </div>,
                )}
              </ThreeColumnGrid>
            </PaneBody>
            <BodyText variant="large" align="center">
              <TextLink to="/terms-and-conditions">
                {intl.formatMessage({
                  id: '/mvgqgV8tt',
                  defaultMessage: 'Read our full Terms & Conditions',
                  description: 'Link text to view full terms and conditions on best price guarantee page',
                })}
              </TextLink>
            </BodyText>
          </LayoutContainer>
        </PaneBody>
      </Pane>
      <Pane>
        <PaneBody size="xl-y md">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading variant="heading3" align="center" as="h2">
                {intl.formatMessage({
                  id: 'hAC2PxCL3V',
                  defaultMessage: 'Frequently Asked Questions',
                  description: 'FAQ section heading on best price guarantee page',
                })}
              </Heading>
              <Faq slug="best-price-guarantee-faqs" />
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
      <Hr />
      <Pane>
        <PaneBody size="xl-y md">
          <VerticalSpacer gap={24}>
            <Heading variant="heading3" align="center" as="h2">
              {intl.formatMessage({
                id: 'noOgCd3hct',
                defaultMessage: 'Found a better price? Talk to us.',
                description: 'Contact section heading on best price guarantee page',
              })}
            </Heading>
            <Group
              direction="horizontal"
              horizontalAlign="center"
              verticalAlign="center"
              gap={16}
            >
              <TextButton
                kind="tertiary"
                href={`tel:${phone.local.number}`}
                rel="nofollow"
              >
                {intl.formatMessage({
                  id: 'd/LAqUeWBZ',
                  defaultMessage: 'Call {phone}',
                  description: 'Button text to call customer support phone number on best price guarantee page',
                }, { phone: phone.local.humanReadable })}
              </TextButton> {intl.formatMessage({
                id: 'LC00hd9jKC',
                defaultMessage: 'or',
                description: 'Separator text between call and online enquiry buttons on best price guarantee page',
              })}
              <div>
                <TextButton
                  kind="primary"
                  to="/frequently-asked-questions#contact-form"
                >
                  {intl.formatMessage({
                    id: 'LIdDFecWBA',
                    defaultMessage: 'Send Online Enquiry',
                    description: 'Button text to navigate to online enquiry form on best price guarantee page',
                  })}
                </TextButton>
              </div>
            </Group>
          </VerticalSpacer>
        </PaneBody>
      </Pane>
      <Pane background="neutral-six">
        <PaneBody size="lg">
          <LayoutContainer>
            <OfferList
              maxOffers={3}
              interstitials={offerListExtras}
              filters={listFilters}
            />
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </>
  )
}

const mapStateToProps = (state: App.State) => ({
  phone: state.geo.currentContact,
})

export default connect(mapStateToProps)(BestPricePage)
